import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { ColumnsType, SorterResult, TableCurrentDataSource, TablePaginationConfig } from 'antd/lib/table/interface';
import Button from 'components/ui/base/button';
import { CommandButton } from '../../../components/ui/composed/dashboard/DashboardTable';
import Table from 'components/ui/base/table/Table';
import { PaginatedParams } from 'core/http/pagination';
import { ListPayload } from 'core/http/response';
import useSession from 'hooks/useSession';
import { FC, useState } from 'react';
import { Individual } from 'store/individuals/individual';
import { Vehicle, VehicleSortParameter } from 'store/vehicles/vehicles';

interface Props {
    data?: ListPayload<Vehicle>;
    onDelete: (ids: string[]) => void;
    onEdit: (id: string) => void;
    onDetails: (id: string) => void;
    handlePagination: (paginator: Partial<PaginatedParams>) => void;
    handleSort: (paginator: Partial<PaginatedParams>) => void;
    loading: boolean;
}

const VehiclesTable: FC<Props> = ({ data, onDelete, onEdit, onDetails, handlePagination, handleSort, loading }) => {
    const [deleteIds, setDeleteIds] = useState<string[]>([]);
    const { userInfo } = useSession();

    const rowSelection = {
        onChange: (selectedRowKeys: unknown, selectedRows: Individual[]) => {
            const ids = selectedRows.map((c) => c.id);
            setDeleteIds(ids);
        },
    };

    const columns: ColumnsType<any> = [
        {
            title: 'Make',
            dataIndex: VehicleSortParameter.MAKE,
            key: VehicleSortParameter.MAKE,
            align: 'left',
            onCell: (record: Vehicle, rowIndex) => {
                return {
                    onClick: () => {
                        onDetails(record.id);
                    },
                };
            },
            render: (text: string, record: Vehicle, index: number) =>
                record.make ? <span>{record.make}</span> : <span>-</span>,
            sorter: true,
        },
        {
            title: 'Status',
            dataIndex: VehicleSortParameter.STATUS,
            key: VehicleSortParameter.STATUS,
            align: 'left',
            render: (text, record: Vehicle) => (record.status ? <span>{record.status}</span> : <span>-</span>),
            sorter: true,
        },
        {
            title: 'Model',
            dataIndex: VehicleSortParameter.MODEL,
            key: VehicleSortParameter.MODEL,
            align: 'left',
            onCell: (record: Vehicle, rowIndex: number | undefined) => {
                return {
                    onClick: () => {
                        onDetails(record.id);
                    },
                };
            },
            render: (text: string, record: Vehicle, index: number) =>
                record.model ? <span>{record.model}</span> : <span>-</span>,
            sorter: true,
        },
        {
            title: 'NUMBER',
            dataIndex: VehicleSortParameter.NUMBER,
            key: VehicleSortParameter.NUMBER,
            align: 'left',
            onCell: (record: Vehicle, rowIndex: number | undefined) => {
                return {
                    onClick: () => {
                        onDetails(record.id);
                    },
                };
            },
            render: (text: string, record: Vehicle, index: number) =>
                record.number ? <span>{record.number}</span> : <span>-</span>,
            sorter: true,
        },
        {
            title: 'Vin',
            dataIndex: VehicleSortParameter.VIN,
            key: VehicleSortParameter.VIN,
            align: 'left',
            onCell: (record: Vehicle, rowIndex: number | undefined) => {
                return {
                    onClick: () => {
                        onDetails(record.id);
                    },
                };
            },
            render: (text: string, record: Vehicle, index: number) =>
                record.vin ? <span>{record.vin}</span> : <span>-</span>,

            sorter: true,
        },
        {
            title: 'Commands',
            dataIndex: 'Commands',
            key: 'commands',
            align: 'center',
            render: (text: string, record: Vehicle, index: number) => {
                return (
                    <>
                        <CommandButton
                            style={{ marginRight: '1.2rem' }}
                            tooltip="Edit"
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record.id)}
                        />
                        <CommandButton
                            style={{ marginRight: '1.2rem' }}
                            tooltip="Delete"
                            icon={<DeleteOutlined />}
                            onClick={() => onDelete([record.id])}
                        />
                    </>
                );
            },
        },
    ];

    function onChange(
        pagination: TablePaginationConfig,
        filters: any,
        sorter: SorterResult<any> | SorterResult<any>[],
        extra: TableCurrentDataSource<any>
    ) {
        if (!Array.isArray(sorter) && sorter.columnKey) {
            const sortParameter = sorter.columnKey?.toString();
            if (sorter.order === 'ascend') {
                handleSort({ sortParameter, sortDirection: 'ASC' });
            } else if (sorter.order === 'descend') {
                handleSort({ sortParameter, sortDirection: 'DESC' });
            }
        }
    }

    return (
        <>
            <Table
                rowKey="id"
                style={{ marginTop: '2.5rem' }}
                columns={columns}
                dataSource={data?.list.filter((v) => v.customerId === userInfo?.customerId)}
                rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                }}
                pagination={{
                    defaultCurrent: 1,
                    total: data?.total,
                    showSizeChanger: false,
                    pageSize: data?.pageSize,
                    position: ['bottomCenter'],
                    onChange: (page: number, size: number | undefined) => {
                        const params = { page: page - 1, size };
                        handlePagination(params);
                    },
                }}
                onChange={onChange}
                loading={loading}
            />
            {deleteIds.length ? (
                <Button
                    type="primary"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        onDelete(deleteIds);
                        setDeleteIds([]);
                    }}
                >
                    delete
                </Button>
            ) : (
                <></>
            )}
        </>
    );
};
export default VehiclesTable;
